import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "@/lib/axiosConfig";
import { FetchedPhotography, Photography, TinyPhotography } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { RelativePosition } from "../dtos";

export const baseRoute = "back/api/photographies";

interface PhotographyDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const mapFetchedPhotography = (
  photography: FetchedPhotography,
): Photography => ({
  ...photography,
  //TECH_DEBT: this should not be parsed
  photosDescriptionDimensions: parseJsonOrUndefined(
    photography.photosDescriptionDimensions,
  ),
});

async function getPhotographies({
  where,
  order,
  take,
  skip,
}: PhotographyDTO): Promise<Photography[]> {
  const { data } = await api.post<FetchedPhotography[]>(
    `back/api/photographies?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data.map(mapFetchedPhotography);
}

export const useGetAllPhotographies = ({
  where,
  order,
  take,
  skip,
}: PhotographyDTO) => {
  return useQuery({
    queryKey: ["photographies", where, order, take, skip],
    queryFn: () => getPhotographies({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/photographies/countByStatus`, {
    where,
  });
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "photography"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const getPhotography = async (id: string) => {
  const { data } = await api.get(`back/api/photographies/${id}`);
  return mapFetchedPhotography(data);
};

export const useGetPhotography = (id?: string) => {
  return useQuery({
    queryKey: ["photography", id],
    queryFn: () => (id ? getPhotography(id) : undefined),
  });
};

async function updatePhotography(photography: Photography) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = photography;
  return await api.patch(`back/api/photographies/${photography.id}`, {
    data: {
      ...rest,
      //TECH_DEBT: This should not be stringified
      photosDescriptionDimensions: JSON.stringify(
        rest.photosDescriptionDimensions,
      ),
    },
  });
}

export function useUpdatePhotography() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePhotography,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["photography", v.id] });
      queryClient.invalidateQueries({ queryKey: ["photographies"] });
    },
  });
}

type CreatePhotographyParams = {
  photosDescriptionNumeroEnregistrement: string;
  photosDescriptionTitre: string;
};
type CreatePhotographyResponse = Pick<Photography, "id">;

const createPhotography = async ({
  photosDescriptionNumeroEnregistrement,
  photosDescriptionTitre,
}: CreatePhotographyParams) => {
  return await api
    .post<CreatePhotographyResponse>("back/api/photographies/create", {
      photosDescriptionNumeroEnregistrement,
      photosDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreatePhotography = (
  options?: UseMutationOptions<
    CreatePhotographyResponse,
    Error,
    CreatePhotographyParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createPhotography,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["photographies"] });
      queryClient.invalidateQueries({ queryKey: ["photography", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "photography"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getTinyPhotographies = async (
  query: Partial<{
    where: object;
    orderBy: object;
    take: number;
    skip: number;
  }>,
) => {
  const { data } = await api.post<TinyPhotography[]>(
    `${baseRoute}/tiny`,
    query,
  );
  return data;
};

export const useGetTinyPhotographies = (
  query: Partial<{
    where: object;
    orderBy: object;
    take: number;
    skip: number;
  }>,
) =>
  useQuery({
    queryKey: ["photographies", query, "tiny"],
    queryFn: () => getTinyPhotographies(query),
    placeholderData: keepPreviousData,
  });

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
